import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const DEFAULT_DESCRIPTION =
  "Up & Away creates handcrafted sculptural fragrance pieces that bring light, scent, and floral artistry into meaningful spaces.";

const ROUTE_SEO = {
  "/": {
    title: "Up & Away | Sculptural Fragrance",
    description: DEFAULT_DESCRIPTION,
  },
  "/contact-us": {
    title: "Collection | Up & Away",
    description:
      "Explore Up & Away handcrafted fragrance sculptures, candles, and custom pieces for homes, hospitality spaces, and special moments.",
  },
  "/our-story": {
    title: "Our Story | Up & Away",
    description:
      "Discover the philosophy behind Up & Away: sculptural fragrance, light, and floral artistry created to lift meaningful spaces.",
  },
  "/reserve-occaission": {
    title: "Reserve an Occasion | Up & Away",
    description:
      "Reserve a custom Up & Away fragrance sculpture or elevated floral moment for weddings, private celebrations, hospitality spaces, and events.",
  },
  "/admin/login": {
    title: "Admin Login | Up & Away",
    description: "Up & Away admin portal.",
    robots: "noindex,nofollow",
  },
  "/admin": {
    title: "Admin | Up & Away",
    description: "Up & Away admin portal.",
    robots: "noindex,nofollow",
  },
};

const getProductTitle = (pathname) => {
  const productSlug = pathname.replace(/^\/products\//, "");
  if (!productSlug) return "Product | Up & Away";

  const productName = decodeURIComponent(productSlug)
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

  return `${productName} | Up & Away`;
};

const getSeoForPath = (pathname) => {
  if (pathname.startsWith("/products/")) {
    return {
      title: getProductTitle(pathname),
      description:
        "View an Up & Away handcrafted fragrance sculpture with sculptural form, gentle light, and meaningful fragrance.",
    };
  }

  return ROUTE_SEO[pathname] || ROUTE_SEO["/"];
};

const setMeta = ({ name, property, content }) => {
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    if (name) element.setAttribute("name", name);
    if (property) element.setAttribute("property", property);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
};

const setCanonical = (href) => {
  let element = document.head.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
};

const setStructuredData = (origin) => {
  const id = "up-away-organization-jsonld";
  let element = document.getElementById(id);

  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Up & Away",
    url: origin,
    description: DEFAULT_DESCRIPTION,
    logo: `${origin}/favicon.png?v=6`,
  });
};

function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = getSeoForPath(pathname);
    const origin = window.location.origin;
    const canonicalUrl = `${origin}${pathname === "/" ? "/" : pathname}`;
    const robots = seo.robots || "index,follow";

    document.title = seo.title;
    setCanonical(canonicalUrl);
    setMeta({ name: "description", content: seo.description });
    setMeta({ name: "robots", content: robots });
    setMeta({ property: "og:url", content: canonicalUrl });
    setMeta({ property: "og:title", content: seo.title });
    setMeta({ property: "og:description", content: seo.description });
    setMeta({ property: "og:type", content: "website" });
    setMeta({ property: "og:site_name", content: "Up & Away" });
    setMeta({ property: "og:image", content: `${origin}/favicon.png?v=6` });
    setMeta({ name: "twitter:card", content: "summary_large_image" });
    setMeta({ name: "twitter:title", content: seo.title });
    setMeta({ name: "twitter:description", content: seo.description });
    setMeta({ name: "twitter:image", content: `${origin}/favicon.png?v=6` });
    setStructuredData(origin);
  }, [pathname]);

  return null;
}

export default Seo;
